import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import AdminWebhookPanel from './AdminWebhookPanel';
import AdminAccessPanel from './AdminAccessPanel';
import { Users, DollarSign, Link2, Crown, Search, Download, Phone, Mail, Calendar, Clock, Shield, Trash2, Edit, Eye } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [adminStats, setAdminStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPlan, setFilterPlan] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const [statsResponse, usersResponse] = await Promise.all([
        axios.get(`${API}/admin/stats`, config),
        axios.get(`${API}/admin/users`, config)
      ]);
      
      setAdminStats(statsResponse.data);
      setUsers(usersResponse.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Erro ao carregar dados administrativos');
    } finally {
      setLoading(false);
    }
  };

  const updateUserPlan = async (userId, newPlan) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/users/${userId}`, {
        subscription_plan: newPlan
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setUsers(users.map(user => 
        user.id === userId ? { ...user, subscription_plan: newPlan } : user
      ));
      
      toast.success('Plano atualizado com sucesso!');
    } catch (error) {
      toast.error('Erro ao atualizar plano');
    }
  };

  const updatePaymentStatus = async (userId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/users/${userId}`, {
        payment_status: status
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setUsers(users.map(user => 
        user.id === userId ? { ...user, payment_status: status } : user
      ));
      
      toast.success('Status de pagamento atualizado!');
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const toggleUserAdmin = async (userId, isAdmin) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/users/${userId}`, {
        is_admin: !isAdmin
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      setUsers(users.map(user => 
        user.id === userId ? { ...user, is_admin: !isAdmin } : user
      ));
      
      toast.success(`Usuário ${!isAdmin ? 'promovido a' : 'removido de'} admin!`);
    } catch (error) {
      toast.error('Erro ao alterar status de admin');
    }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Tem certeza que deseja excluir o usuário ${email}?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUsers(users.filter(user => user.id !== userId));
      toast.success('Usuário excluído com sucesso!');
    } catch (error) {
      toast.error('Erro ao excluir usuário');
    }
  };

  const exportUsers = () => {
    const csvContent = [
      ['Nome', 'Email', 'Telefone', 'Plano', 'Status Pagamento', 'Trial Restante', 'Data Cadastro'].join(','),
      ...filteredUsers.map(u => [
        u.name || '-',
        u.email,
        u.phone || '-',
        u.subscription_plan,
        u.payment_status,
        u.trial_days_left > 0 ? `${u.trial_days_left} dias` : '-',
        u.created_at ? new Date(u.created_at).toLocaleDateString('pt-BR') : '-'
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `usuarios_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Exportação realizada!');
  };

  // Filtrar usuários
  const filteredUsers = users.filter(user => {
    const matchSearch = 
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phone?.includes(searchTerm);
    
    const matchPlan = filterPlan === 'all' || user.subscription_plan === filterPlan;
    
    return matchSearch && matchPlan;
  });

  // Estatísticas rápidas
  const stats = {
    total: users.length,
    freeTrial: users.filter(u => u.subscription_plan === 'free_trial').length,
    paid: users.filter(u => u.payment_status === 'paid' && u.subscription_plan !== 'free_trial').length,
    trialExpiring: users.filter(u => u.subscription_plan === 'free_trial' && u.trial_days_left <= 2).length
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const getPlanBadge = (plan) => {
    const badges = {
      'free_trial': 'bg-green-100 text-green-800',
      'free': 'bg-gray-100 text-gray-800',
      'starter': 'bg-blue-100 text-blue-800',
      'pro': 'bg-purple-100 text-purple-800',
      'enterprise': 'bg-yellow-100 text-yellow-800'
    };
    const names = {
      'free_trial': '🎁 Trial',
      'free': 'Grátis',
      'starter': '⭐ Starter',
      'pro': '🚀 Pro',
      'enterprise': '👑 Enterprise'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[plan] || badges.free}`}>
        {names[plan] || plan}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const badges = {
      'paid': 'bg-green-100 text-green-800',
      'active': 'bg-green-100 text-green-800',
      'pending': 'bg-yellow-100 text-yellow-800',
      'trial_expired': 'bg-red-100 text-red-800'
    };
    const names = {
      'paid': '✅ Pago',
      'active': '✅ Ativo',
      'pending': '⏳ Pendente',
      'trial_expired': '❌ Expirado'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status] || 'bg-gray-100 text-gray-800'}`}>
        {names[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <Shield className="h-8 w-8 mr-3 text-blue-600" />
          Painel Administrativo
        </h1>
        <p className="text-gray-600">Gerencie todos os usuários do MonetizeMaster</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
          <div className="flex items-center">
            <Users className="h-8 w-8 text-blue-500" />
            <div className="ml-3">
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-xs text-gray-500">Total Usuários</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-green-500" />
            <div className="ml-3">
              <p className="text-2xl font-bold text-gray-900">{stats.freeTrial}</p>
              <p className="text-xs text-gray-500">Em Trial</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
          <div className="flex items-center">
            <DollarSign className="h-8 w-8 text-purple-500" />
            <div className="ml-3">
              <p className="text-2xl font-bold text-gray-900">{stats.paid}</p>
              <p className="text-xs text-gray-500">Pagantes</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-red-500">
          <div className="flex items-center">
            <Calendar className="h-8 w-8 text-red-500" />
            <div className="ml-3">
              <p className="text-2xl font-bold text-gray-900">{stats.trialExpiring}</p>
              <p className="text-xs text-gray-500">Trial Expirando</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex items-center gap-4 w-full md:w-auto">
            <div className="relative flex-1 md:w-80">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome, email ou telefone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <select
              value={filterPlan}
              onChange={(e) => setFilterPlan(e.target.value)}
              className="border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Todos os Planos</option>
              <option value="free_trial">Trial</option>
              <option value="starter">Starter</option>
              <option value="pro">Pro</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>
          
          <button
            onClick={exportUsers}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            <Download className="h-4 w-4" />
            Exportar CSV
          </button>
        </div>
      </div>

      {/* Cadastros e liberação de acesso (10/30 dias) */}
      <AdminAccessPanel users={users} onChanged={fetchAdminData} />

      {/* Webhook Mercado Pago */}
      <AdminWebhookPanel />

      {/* Users Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">
            Usuários Cadastrados ({filteredUsers.length})
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuário</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contato</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plano</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trial</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cadastro</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUsers.map(user => (
                <tr key={user.id} className="hover:bg-gray-50">
                  {/* Usuário */}
                  <td className="px-4 py-3">
                    <div>
                      <div className="font-medium text-gray-900 flex items-center">
                        {user.name || 'Sem nome'}
                        {user.is_admin && (
                          <span className="ml-2 px-1.5 py-0.5 bg-red-100 text-red-800 text-xs rounded">ADMIN</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 flex items-center">
                        <Mail className="h-3 w-3 mr-1" />
                        {user.email}
                      </div>
                    </div>
                  </td>
                  
                  {/* Contato */}
                  <td className="px-4 py-3">
                    <div className="text-sm">
                      {user.phone ? (
                        <a href={`tel:${user.phone}`} className="flex items-center text-blue-600 hover:underline">
                          <Phone className="h-3 w-3 mr-1" />
                          {user.phone}
                        </a>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                  </td>
                  
                  {/* Plano */}
                  <td className="px-4 py-3">
                    <select
                      value={user.subscription_plan}
                      onChange={(e) => updateUserPlan(user.id, e.target.value)}
                      className="text-xs border rounded px-2 py-1 bg-transparent"
                    >
                      <option value="free_trial">🎁 Trial</option>
                      <option value="free">Grátis</option>
                      <option value="starter">⭐ Starter</option>
                      <option value="pro">🚀 Pro</option>
                      <option value="enterprise">👑 Enterprise</option>
                    </select>
                  </td>
                  
                  {/* Status */}
                  <td className="px-4 py-3">
                    <select
                      value={user.payment_status}
                      onChange={(e) => updatePaymentStatus(user.id, e.target.value)}
                      className={`text-xs border rounded px-2 py-1 ${
                        user.payment_status === 'paid' ? 'bg-green-50 border-green-300' :
                        user.payment_status === 'pending' ? 'bg-yellow-50 border-yellow-300' :
                        user.payment_status === 'trial_expired' ? 'bg-red-50 border-red-300' :
                        'bg-gray-50'
                      }`}
                    >
                      <option value="paid">✅ Pago</option>
                      <option value="pending">⏳ Pendente</option>
                      <option value="trial_expired">❌ Expirado</option>
                    </select>
                  </td>
                  
                  {/* Trial */}
                  <td className="px-4 py-3">
                    {user.subscription_plan === 'free_trial' ? (
                      <div className={`text-sm font-medium ${
                        user.trial_days_left <= 2 ? 'text-red-600' : 
                        user.trial_days_left <= 4 ? 'text-yellow-600' : 'text-green-600'
                      }`}>
                        {user.trial_expired ? (
                          <span className="text-red-600">Expirado</span>
                        ) : (
                          <span>{user.trial_days_left} dias</span>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  
                  {/* Data Cadastro */}
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {formatDate(user.created_at)}
                  </td>
                  
                  {/* Ações */}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedUser(user)}
                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                        title="Ver detalhes"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => toggleUserAdmin(user.id, user.is_admin)}
                        className={`p-1 rounded ${user.is_admin ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'}`}
                        title={user.is_admin ? 'Remover admin' : 'Tornar admin'}
                      >
                        <Crown className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => deleteUser(user.id, user.email)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title="Excluir usuário"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredUsers.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Nenhum usuário encontrado
          </div>
        )}
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedUser(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4 flex items-center">
              <Users className="h-6 w-6 mr-2 text-blue-600" />
              Detalhes do Usuário
            </h3>
            
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500">Nome</label>
                  <p className="font-medium">{selectedUser.name || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Email</label>
                  <p className="font-medium">{selectedUser.email}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Telefone</label>
                  <p className="font-medium">{selectedUser.phone || '-'}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Plano</label>
                  <p>{getPlanBadge(selectedUser.subscription_plan)}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Status Pagamento</label>
                  <p>{getStatusBadge(selectedUser.payment_status)}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Admin</label>
                  <p className="font-medium">{selectedUser.is_admin ? '✅ Sim' : '❌ Não'}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Links Criados</label>
                  <p className="font-medium">{selectedUser.link_count || 0}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Conteúdos IA</label>
                  <p className="font-medium">{selectedUser.ai_content_count || 0}</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500">Data Cadastro</label>
                  <p className="font-medium">{formatDate(selectedUser.created_at)}</p>
                </div>
                {selectedUser.subscription_plan === 'free_trial' && (
                  <>
                    <div>
                      <label className="text-xs text-gray-500">Trial Expira</label>
                      <p className="font-medium">{formatDate(selectedUser.trial_end)}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500">Dias Restantes</label>
                      <p className={`font-medium ${selectedUser.trial_days_left <= 2 ? 'text-red-600' : 'text-green-600'}`}>
                        {selectedUser.trial_expired ? 'Expirado' : `${selectedUser.trial_days_left} dias`}
                      </p>
                    </div>
                  </>
                )}
                {selectedUser.paid_at && (
                  <div>
                    <label className="text-xs text-gray-500">Data Pagamento</label>
                    <p className="font-medium">{formatDate(selectedUser.paid_at)}</p>
                  </div>
                )}
              </div>
            </div>
            
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setSelectedUser(null)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
